/*
 * <predictive-search-client>
 * ------------------------------------------------------------------
 * Header dropdown search: runs Shopify's NATIVE search on each debounced
 * keystroke via the Section Rendering API:
 * /search?q=<term>&type=product&options[prefix]=last&section_id=predictive-search-dropdown
 * The server executes the full search engine (search.results: typo tolerance,
 * S&D synonyms/boosts, no 10-result API cap) and returns
 * sections/predictive-search-dropdown.liquid pre-rendered — this class only
 * extracts the [data-predictive-search-dropdown] markup and injects it. It
 * subclasses the theme's PredictiveSearch so keyboard navigation, open/close,
 * focus, debounce (300ms via SearchForm), caching and a11y live-region logic
 * are reused — only getSearchResults() is overridden.
 *
 * Search scope is gated by settings.search_source_collection inside the
 * dropdown section (all products when blank). The /search results page
 * (sections/main-search.liquid) renders the same native, gated results
 * server-side, so the dropdown previews the top N of that page. Descriptions
 * are always matched (native engine default). Custom search keywords and
 * synonyms are configured in the Search & Discovery app (Synonyms / Product
 * boosts), not in the theme.
 * Note: these background fetches do NOT count in Shopify's "searches" report —
 * only committed searches (navigating to /search) are recorded.
 */

(function () {
  function escapeHtml(value) {
    return (value == null ? '' : String(value)).replace(/[&<>"']/g, (character) => {
      switch (character) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        default:
          return '&#39;';
      }
    });
  }

  // --- GA4/GTM journey tracking helpers --------------------------------------
  // Direct window.dataLayer pushes (see docs/search-tracking-pixel-substitution.md
  // for why this is preferred over a Shopify custom web pixel). Elevar loads GTM
  // at runtime and reads this same plain array (precedent: rebuy-extensions.js).
  function pushToDataLayer(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  // Short id shared by every event belonging to one settled search query, so a
  // GA4 Exploration can stitch impression -> select_item -> view_item -> purchase.
  function generateSearchId() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  // Reads the always-rendered data-product-* attributes (added in
  // sections/predictive-search-dropdown.liquid) off each product link inside a
  // settled results panel and shapes them as GA4 ecommerce items.
  function collectItemsFromPanel(panel) {
    return Array.from(panel.querySelectorAll('[data-product-id]')).map((link) => ({
      item_id: link.dataset.productId,
      item_name: link.dataset.productTitle,
      index: Number(link.dataset.position),
      price: Number(link.dataset.productPrice),
    }));
  }

  // --- Header dropdown ------------------------------------------------------
  class PredictiveSearchClient extends PredictiveSearch {
    constructor() {
      super();

      // Re-scope the cross-instance cache list to our own tag so the shared
      // cachedResults map is synced across every <predictive-search-client>.
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search-client');

      this.minSearchLength = parseInt(this.dataset.minSearchLength, 10) || 1;

      // --- Journey tracking state (see docs/search-tracking-gtm-setup.md) -----
      // search_id/search_term/result_count describe the current SETTLED query;
      // settled tracks whether it has been shown for >=800ms uninterrupted;
      // resolved tracks whether it was already clicked or submitted, which
      // suppresses the abandoned event for that search_id.
      this.searchId = null;
      this.trackedSearchTerm = null;
      this.trackedResultCount = null;
      this.settled = false;
      this.resolved = false;
      this.pendingSettleTimer = null;
      this.pendingSettleFlush = null;
      this.lastSettledTerm = null;

      this.addEventListener('click', this.onSuggestionClick.bind(this));
      // pagehide covers a hard navigation/tab-close with the dropdown still open.
      window.addEventListener('pagehide', this.pushAbandonedIfPending.bind(this));

      // Localized strings (with placeholders) passed from Liquid — used only by
      // the client-built panels (keep-typing prompt, network-error fallback);
      // normal results arrive fully rendered.
      this.searchLabel = this.dataset.searchLabel || 'Search';
      this.productsLabel = this.dataset.productsLabel || 'Products';
      this.searchForText = this.dataset.searchForText || 'Search for "[terms]"';
      this.noResultsText = this.dataset.noResultsText || 'No results';
      this.keepTypingText = this.dataset.keepTypingText || 'Please continue typing to see results';

      // Resolve the a11y live region up front so showEmptyState() (which renders
      // without going through setLiveRegionLoadingState) always has it available.
      this.statusElement = this.querySelector('.predictive-search-status');

      // Optional merchant-curated empty state (suggested searches + a collection),
      // rendered once at page load into this <template> and shown before typing.
      this.emptyStateTemplate = this.querySelector('[data-predictive-search-empty-template]');
    }

    // Inject the curated empty state and open the dropdown; returns false when
    // nothing is curated so callers fall back to the default (closed) behavior.
    showEmptyState() {
      // The curated empty state bypasses renderSearchResults() entirely, so cancel
      // any pending impression/zero push directly here — no tracking event fires
      // for this panel.
      this.cancelPendingSettle();
      if (!this.emptyStateTemplate) return false;
      this.predictiveSearchResults.innerHTML = this.emptyStateTemplate.innerHTML;
      this.removeAttribute('loading');
      this.open();
      return true;
    }

    // Below the merchant-set minimum (settings.search_min_chars): a fully empty
    // input still gets the curated empty state, while a too-short typed term shows
    // a friendly keep-typing prompt — in both cases no request is made.
    showBelowMinimumState(query) {
      if (!query.length) {
        this.showEmptyState();
        return;
      }
      this.renderSearchResults(this.buildKeepTypingMarkup());
    }

    // Prompt shown in place of results while the term is below the minimum length.
    // Plain <p> (not <li>) so the keyboard navigation in PredictiveSearch skips it.
    // No listbox role here: a listbox without option children is invalid ARIA;
    // the prompt is announced through the live region instead.
    buildKeepTypingMarkup() {
      return `<div id="predictive-search-results">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search-full__groups">
          <div class="predictive-search__result-group predictive-search-full__group">
            <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">${escapeHtml(
              this.productsLabel
            )}</h2>
            <p class="predictive-search__keep-typing">${escapeHtml(this.keepTypingText)}</p>
          </div>
        </div>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>${escapeHtml(this.keepTypingText)}</span>`;
    }

    onChange() {
      super.onChange();
      const query = this.getQuery();
      if (query.length < this.minSearchLength) this.showBelowMinimumState(query);
    }

    onFocus() {
      super.onFocus();
      const query = this.getQuery();
      if (query.length < this.minSearchLength) this.showBelowMinimumState(query);
    }

    getSearchResults(searchTerm) {
      const term = (searchTerm || '').trim();
      if (term.length < this.minSearchLength) {
        this.showBelowMinimumState(term);
        return;
      }

      const queryKey = term.replace(/\s+/g, '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      // Cancel any in-flight request so only the latest query renders.
      this.abortController.abort();
      this.abortController = new AbortController();
      this.setLiveRegionLoadingState();

      this.fetchResultsMarkup(term, this.abortController.signal)
        .then((markup) => {
          // Keep the cache synced across instances (mirrors base PredictiveSearch).
          this.allPredictiveSearchInstances.forEach((instance) => {
            instance.cachedResults[queryKey] = markup;
          });
          this.renderSearchResults(markup);
        })
        .catch((error) => {
          // A superseded request was aborted — let the newer one win.
          if (error && (error.name === 'AbortError' || error.code === 20)) return;
          // Any other failure: show an explicit empty panel instead of a spinner.
          this.renderSearchResults(this.buildFallbackMarkup(term));
        });
    }

    // Build the locale-aware Section Rendering API URL: Shopify runs its native
    // search server-side and returns sections/predictive-search-dropdown.liquid
    // (search.results, collection-gated) pre-rendered.
    buildSectionUrl(term) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const params = new URLSearchParams();
      params.set('q', term);
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
      params.set('section_id', 'predictive-search-dropdown');
      return `${root}search?${params.toString()}`;
    }

    // Fetch the rendered section and extract the dropdown markup. Retries once
    // on 429/503 honoring Retry-After (/search throttles harder than /suggest).
    fetchResultsMarkup(term, signal, retried) {
      return fetch(this.buildSectionUrl(term), { signal }).then((response) => {
        if ((response.status === 429 || response.status === 503) && !retried) {
          const wait = (parseInt(response.headers.get('Retry-After'), 10) || 1) * 1000;
          return new Promise((resolve, reject) => {
            setTimeout(() => this.fetchResultsMarkup(term, signal, true).then(resolve, reject), wait);
          });
        }
        if (!response.ok) throw new Error(response.status);
        return response.text().then((text) => {
          const wrapper = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('[data-predictive-search-dropdown]');
          if (!wrapper) throw new Error('Dropdown section markup not found in response');
          return wrapper.innerHTML;
        });
      });
    }

    // Minimal "no results" panel shown only when the section fetch fails.
    buildFallbackMarkup(searchTerm) {
      const searchFor = this.searchForText.replace('[terms]', escapeHtml(searchTerm));
      const countText = this.noResultsText.replace('[terms]', searchTerm);

      return `<div id="predictive-search-results" role="listbox" aria-label="${escapeHtml(this.searchLabel)}">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search-full__groups"></div>
        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <button id="predictive-search-option-search-keywords-button" class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false" type="submit">
            <span data-predictive-search-search-for-text>${searchFor}</span>
          </button>
        </div>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>${escapeHtml(countText)}</span>`;
    }

    // --- GA4/GTM journey tracking --------------------------------------------
    // Renders happen on every debounced keystroke; only a REAL results panel
    // (identified by data-search-term, added in predictive-search-dropdown.liquid)
    // is eligible to settle into an impression/zero-results push. The
    // keep-typing prompt, curated empty state and fetch-failure fallback all
    // lack that attribute, so they fall through and never push.
    renderSearchResults(markup) {
      super.renderSearchResults(markup);
      this.cancelPendingSettle();

      const panel = this.querySelector('#predictive-search-results[data-search-term]');
      if (!panel) return;

      const term = panel.dataset.searchTerm;
      // Cache-hit re-render of the same already-settled term: don't re-push.
      if (term === this.lastSettledTerm) return;

      const resultCount = parseInt(panel.dataset.resultCount, 10) || 0;
      const flush = () => this.settleQuery(panel, term, resultCount);
      this.pendingSettleFlush = flush;
      this.pendingSettleTimer = setTimeout(() => {
        this.pendingSettleTimer = null;
        this.pendingSettleFlush = null;
        flush();
      }, 800);
    }

    // Cancels a scheduled (not-yet-fired) settle push without pushing anything —
    // used when a newer render or a dropdown close supersedes it.
    cancelPendingSettle() {
      if (!this.pendingSettleTimer) return;
      clearTimeout(this.pendingSettleTimer);
      this.pendingSettleTimer = null;
      this.pendingSettleFlush = null;
    }

    // Forces a scheduled settle push to fire immediately, synchronously, so a
    // click/commit event that happens before the 800ms timer elapses still
    // lands AFTER the impression/zero-results event in the dataLayer.
    flushPendingSettle() {
      if (!this.pendingSettleFlush) return;
      clearTimeout(this.pendingSettleTimer);
      const flush = this.pendingSettleFlush;
      this.pendingSettleTimer = null;
      this.pendingSettleFlush = null;
      flush();
    }

    // Marks a query as settled: assigns the search_id shared by every event for
    // this query, then pushes the impression (or zero-results) event.
    settleQuery(panel, term, resultCount) {
      this.searchId = generateSearchId();
      this.trackedSearchTerm = term;
      this.trackedResultCount = resultCount;
      this.settled = true;
      this.resolved = false;
      this.lastSettledTerm = term;

      if (resultCount > 0) {
        // Reset the ecommerce object first (Google's documented GTM pattern) so
        // its recursive dataLayer merge can't leak a previous event's items into
        // this one — see docs/search-tracking-gtm-setup.md.
        pushToDataLayer({ ecommerce: null });
        pushToDataLayer({
          event: 'uq_search_impression',
          search_id: this.searchId,
          search_term: term,
          result_count: resultCount,
          ecommerce: {
            item_list_name: 'predictive_search',
            currency: panel.dataset.currency,
            items: collectItemsFromPanel(panel),
          },
        });
      } else {
        pushToDataLayer({
          event: 'uq_search_zero_results',
          search_id: this.searchId,
          search_term: term,
          result_count: 0,
        });
      }
    }

    // Delegated click listener covering the product <a> links (keyboard select
    // calls .click() on the same <a> via SearchForm's selectOption(), so this
    // single listener covers both mouse and keyboard selection). Does not call
    // preventDefault — navigation proceeds; the push happens synchronously first.
    onSuggestionClick(event) {
      const link = event.target.closest('[data-product-id]');
      if (!link || !this.contains(link)) return;

      this.flushPendingSettle();
      this.resolved = true;

      // Reset the ecommerce object first (Google's documented GTM pattern) so
      // its recursive dataLayer merge can't leak the impression's items array
      // into this event's single-item payload.
      pushToDataLayer({ ecommerce: null });
      pushToDataLayer({
        event: 'uq_search_suggestion_click',
        search_id: this.searchId,
        search_term: this.trackedSearchTerm,
        ecommerce: {
          item_list_name: 'predictive_search',
          items: [
            {
              item_id: link.dataset.productId,
              item_name: link.dataset.productTitle,
              index: Number(link.dataset.position),
              price: Number(link.dataset.productPrice),
            },
          ],
        },
      });
    }

    // Real form submit / "Search for …" commit. Calls the base class first so
    // its existing preventDefault logic (blocking empty or option-selected
    // submits) is preserved untouched.
    onFormSubmit(event) {
      this.flushPendingSettle();
      super.onFormSubmit(event);
      // The base class itself calls preventDefault() for an empty query or when
      // a product option is still aria-selected — that's not a real commit, so
      // don't push (and don't suppress the pending abandoned event) for it.
      if (event.defaultPrevented) return;
      this.resolved = true;

      pushToDataLayer({
        event: 'uq_search_committed',
        search_id: this.searchId,
        search_term: this.getQuery(),
      });
    }

    // At most once per search_id: fires when a settled query WITH results
    // closes without a suggestion click or a form submit having happened. A
    // settled query with zero results already got its own
    // uq_search_zero_results event, so it's excluded here to avoid
    // double-counting the same session as both zero-results and abandoned.
    pushAbandonedIfPending() {
      this.cancelPendingSettle();
      if (!this.settled || this.resolved || !this.searchId) return;
      if (!(this.trackedResultCount > 0)) return;

      pushToDataLayer({
        event: 'uq_search_abandoned',
        search_id: this.searchId,
        search_term: this.trackedSearchTerm,
        result_count: this.trackedResultCount,
      });
      this.resolved = true;
    }

    // Base close path shared by blur (onFocusOut), Escape/clear and programmatic
    // closes — the dropdown-closed half of the abandoned-event trigger. Reset
    // lastSettledTerm so a later, separate session (after this one has fully
    // closed) that types the SAME term again still settles and pushes — the
    // dedupe in renderSearchResults is only meant to cover cache-hit re-renders
    // within a single still-open dropdown session, not across sessions.
    close(clearSearchTerm = false) {
      this.pushAbandonedIfPending();
      this.lastSettledTerm = null;
      super.close(clearSearchTerm);
    }
  }

  customElements.define('predictive-search-client', PredictiveSearchClient);
})();
